import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../common/SafeIcon";
import PriorityBadge from "./PriorityBadge";

const { FiFilter, FiX, FiChevronDown } = FiIcons;

const PriorityFilter = ({ selectedPriorities, onPriorityChange, onClearFilters }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const priorities = ["urgent", "high", "medium", "low"];
  const hasFilters = selectedPriorities.length > 0;

  const handlePriorityToggle = (priority) => {
    if (selectedPriorities.includes(priority)) {
      onPriorityChange(selectedPriorities.filter((p) => p !== priority));
    } else {
      onPriorityChange([...selectedPriorities, priority]);
    }
  };

  const handleClearFilters = () => {
    onClearFilters();
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm font-medium ${
          hasFilters
            ? "border-violet-500/40 text-violet-300"
            : "border-white/10 text-gray-400 hover:text-white hover:border-white/20"
        }`}
        style={{ background: hasFilters ? "rgba(124,58,237,0.15)" : "rgba(255,255,255,0.05)" }}
      >
        <SafeIcon icon={FiFilter} className="w-4 h-4" />
        <span>
          Priority
          {hasFilters && (
            <span className="ml-1.5 px-1.5 py-0.5 bg-violet-600 text-white rounded-full text-xs">
              {selectedPriorities.length}
            </span>
          )}
        </span>
        <SafeIcon
          icon={FiChevronDown}
          className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 sm:left-auto sm:right-0 mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-xl shadow-2xl z-50"
            style={{
              background: "#1a1a28",
              border: "1px solid rgba(255,255,255,0.10)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.6), 0 0 40px rgba(124,58,237,0.08)",
            }}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-white text-sm">Filter by Priority</h3>
                {hasFilters && (
                  <button
                    onClick={handleClearFilters}
                    className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    <SafeIcon icon={FiX} className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>

              <div className="space-y-1.5">
                {priorities.map((priority) => (
                  <motion.button
                    key={priority}
                    onClick={() => handlePriorityToggle(priority)}
                    className={`w-full flex items-center justify-between p-2.5 rounded-lg transition-all border ${
                      selectedPriorities.includes(priority)
                        ? "border-violet-500/40 bg-violet-900/20"
                        : "border-transparent hover:bg-white/5"
                    }`}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <PriorityBadge priority={priority} />
                    {selectedPriorities.includes(priority) && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="w-4 h-4 bg-violet-600 rounded-full flex items-center justify-center"
                      >
                        <SafeIcon icon={FiX} className="w-2 h-2 text-white" />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>

              {hasFilters && (
                <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="text-xs text-gray-500">
                    {selectedPriorities.length} filter{selectedPriorities.length !== 1 ? "s" : ""} active
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PriorityFilter;
